import { safeDecodeHtmlEntities } from 'src/app/core/helpers/text-sanitizer';
import { ECustomFieldType } from 'src/app/project/modules/custom-fields/custom-field-types-enums';
import { GET_CUSTOM_FIELDS } from 'src/app/project/modules/custom-fields/custom-fields.store';
import { EQuickSortTranslatedKey } from 'src/app/project/modules/filters/filters-api/sort/quick-sort.consts';
import { getEmptyGroupName } from 'src/app/project/modules/site/site-table/grouping/empty-group-name';
import { transformDate } from 'src/app/project/shared/date-transformer';
import { EBasicField } from 'src/app/project/shared/enums/basic-fields-enums';
import { ELocalStorageItems } from 'src/app/project/shared/enums/local-storage-items.enum';
import { TGroupSizesResponse, TPartialCount } from './points-group-sizes.consts';

type TNormaliser = (value: string) => string;

const NUMERIC_CUSTOM_FIELD_TYPES: ReadonlySet<ECustomFieldType> = new Set([
  ECustomFieldType.COST,
  ECustomFieldType.NUMBERS,
  ECustomFieldType.PERCENTAGE,
  ECustomFieldType.TIME,
]);

function normaliseDateValue(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  if (/^\d+$/.test(value)) {
    return transformDate({ value });
  }
  return value;
}

function normaliseArrayValue(value: string): string {
  const inner = value.replace(/^\[|\]$/g, '').trim();
  if (inner === '') {
    return '[]';
  }
  return `[${inner
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .sort()
    .join(',')}]`;
}

function normaliseNumericValue(value: string): string {
  if (value === '') {
    return '';
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return String(parsed);
  }
  return value;
}

function normaliseTimelineValue(value: string): string {
  if (value === '') {
    return '';
  }
  // FE renders the range as "YYYY-MM-DD - YYYY-MM-DD"; BE keys the partial count by
  // the start date alone. Take everything before the first " - " separator.
  const separatorIndex = value.indexOf(' - ');
  return separatorIndex === -1 ? value : value.slice(0, separatorIndex).trim();
}

function getNormaliser(groupingKey: string): TNormaliser | null {
  if (
    groupingKey === EQuickSortTranslatedKey.CREATED ||
    groupingKey === EQuickSortTranslatedKey.UPDATED
  ) {
    return normaliseDateValue;
  }

  if (
    groupingKey === EQuickSortTranslatedKey.SITE ||
    groupingKey === EQuickSortTranslatedKey.ACCOUNT
  ) {
    return safeDecodeHtmlEntities;
  }

  const lowerKey = groupingKey.toLowerCase();

  if (
    lowerKey === EBasicField.ASSIGNEES.toLowerCase() ||
    lowerKey === EBasicField.TAGS.toLowerCase()
  ) {
    return normaliseArrayValue;
  }

  const customField = Object.values(GET_CUSTOM_FIELDS()).find(
    (cf) => cf.id.toLowerCase() === lowerKey,
  );

  if (!customField) {
    return null;
  }

  if (NUMERIC_CUSTOM_FIELD_TYPES.has(customField.type)) {
    return normaliseNumericValue;
  }

  switch (customField.type) {
    case ECustomFieldType.DATE:
      return normaliseDateValue;
    case ECustomFieldType.MULTI_LIST:
      return normaliseArrayValue;
    case ECustomFieldType.TIMELINE:
      return normaliseTimelineValue;
    default:
      return null;
  }
}

export function isBooleanGroupKey(key: string): boolean {
  if (key === EBasicField.FLAGGED) {
    return true;
  }
  const lowerKey = key.toLowerCase();
  return Object.values(GET_CUSTOM_FIELDS()).some(
    (cf) => cf.id.toLowerCase() === lowerKey && cf.type === ECustomFieldType.CHECKBOX,
  );
}

function checkValueMatch(
  partialCountValue: string,
  itemValue: string,
  groupingKey: string,
): boolean {
  if (partialCountValue === itemValue) {
    return true;
  }

  // The FE "(Empty)" placeholder matches a BE empty string regardless of column type,
  // so this must run before the typed branches below — otherwise they short-circuit
  // and a typed column's empty group renders "..." instead of the BE count.
  const isEmptyStringMatch =
    (partialCountValue === getEmptyGroupName() && itemValue === '') ||
    (partialCountValue === '' && itemValue === getEmptyGroupName());
  if (isEmptyStringMatch) {
    return true;
  }

  const normaliser = getNormaliser(groupingKey);
  if (normaliser) {
    return normaliser(partialCountValue) === normaliser(itemValue);
  }

  const isFalsyMatch = itemValue === '' && partialCountValue === 'false';
  return isFalsyMatch && isBooleanGroupKey(groupingKey);
}

export function groupByMatches(
  partialCountGroupBy: Record<string, string>,
  itemGroupBy: Record<string, string>,
): boolean {
  const keys = Object.keys(partialCountGroupBy);

  if (keys.length !== Object.keys(itemGroupBy).length) {
    return false;
  }

  return keys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(itemGroupBy, key) &&
      checkValueMatch(partialCountGroupBy[key], itemGroupBy[key], key),
  );
}

let cachedRaw: string | null = null;
let cachedParsed: TGroupSizesResponse | undefined;

export function readCachedGroupSizes(): TGroupSizesResponse | undefined {
  const groupString = localStorage.getItem(ELocalStorageItems.GROUP_SIZES);

  if (!groupString?.length) {
    return undefined;
  }

  if (groupString !== cachedRaw) {
    cachedRaw = groupString;
    cachedParsed = JSON.parse(groupString) as TGroupSizesResponse;
  }

  return cachedParsed;
}

export function findMatchingPartialCount(
  groupBy: Record<string, string>,
  cachedGroupSizes?: TGroupSizesResponse,
): TPartialCount | undefined {
  const sizes = cachedGroupSizes ?? readCachedGroupSizes();

  if (!sizes?.partialCounts?.length || !groupBy) {
    return undefined;
  }

  return sizes.partialCounts.find((partialCount) => groupByMatches(partialCount.groupBy, groupBy));
}
